import React, { useState, useEffect } from 'react';
import { Box, Typography, Stack, IconButton, Chip, Fade, Button, Dialog, DialogContent } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BoltIcon from '@mui/icons-material/Bolt';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import BusinessIcon from '@mui/icons-material/Business';
import HandshakeIcon from '@mui/icons-material/Handshake';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import EmailIcon from '@mui/icons-material/Email';
import NoteIcon from '@mui/icons-material/Note';
import PhoneIcon from '@mui/icons-material/Phone';
import LinkIcon from '@mui/icons-material/Link';
import { useTranslation } from 'react-i18next';
import { glassCard, ACCENT, ACCENT_DARK, TEXT_PRIMARY, TEXT_SECONDARY, TEXT_TERTIARY, DIALOG_PAPER, noMotion } from '../../designsystem/tokens';

const ENTITY_ICONS = {
    deal: <HandshakeIcon sx={{ fontSize: 18, color: ACCENT }} />,
    contact: <PersonIcon sx={{ fontSize: 18, color: ACCENT }} />,
    company: <BusinessIcon sx={{ fontSize: 18, color: ACCENT }} />,
    task: <TaskAltIcon sx={{ fontSize: 18, color: ACCENT }} />,
    meeting: <EventIcon sx={{ fontSize: 18, color: ACCENT }} />,
    email: <EmailIcon sx={{ fontSize: 18, color: ACCENT }} />,
    note: <NoteIcon sx={{ fontSize: 18, color: ACCENT }} />,
    call: <PhoneIcon sx={{ fontSize: 18, color: ACCENT }} />,
};

const ADD_ENTITY_TYPES = [
    { key: 'deal', icon: <HandshakeIcon sx={{ fontSize: 18, color: ACCENT }} /> },
    { key: 'contact', icon: <PersonIcon sx={{ fontSize: 18, color: ACCENT }} /> },
    { key: 'company', icon: <BusinessIcon sx={{ fontSize: 18, color: ACCENT }} /> },
    { key: 'task', icon: <TaskAltIcon sx={{ fontSize: 18, color: ACCENT }} /> },
    { key: 'meeting', icon: <EventIcon sx={{ fontSize: 18, color: ACCENT }} /> },
    { key: 'email', icon: <EmailIcon sx={{ fontSize: 18, color: ACCENT }} /> },
    { key: 'note', icon: <NoteIcon sx={{ fontSize: 18, color: ACCENT }} /> },
    { key: 'call', icon: <PhoneIcon sx={{ fontSize: 18, color: ACCENT }} /> },
];

const fmtDate = (v) => {
    if (!v) return null;
    try { return new Date(v).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }); } catch { return v; }
};

/**
 * ReviewStepV2 — review CRMObject[] generically.
 * Shows each CRMObject as a card with its display name, core property values,
 * and an edit button to jump back to that step.
 */
const ReviewStepV2 = ({ objects, onEditStep, onAddEntity, loaded, oneShotMode }) => {
    const { t } = useTranslation();
    const [addDialogOpen, setAddDialogOpen] = useState(false);

    useEffect(() => {
        if (loaded) loaded();
    }, [loaded]);

    const getFields = (obj) => {
        const fields = [];
        const mainProps = (obj.properties || []).filter(p => p.core && p.group === 'main');
        // First field = display name, second = secondary info
        for (const prop of mainProps.slice(0, 3)) {
            if (prop.value != null && prop.value !== '') {
                let display = prop.value;
                if (prop.type === 'number' && prop.name === 'amount') display = `€${prop.value}`;
                if (prop.type === 'date' || prop.type === 'datetime') display = fmtDate(prop.value);
                fields.push(String(display));
            }
        }
        return fields;
    };

    const getMandatoryFilled = (obj) => {
        return (obj.properties || [])
            .filter(p => {
                if (p.mandatory) return true;
                if (p.mandatoryIf) {
                    const dep = (obj.properties || []).find(d => d.name === p.mandatoryIf);
                    return dep && dep.value != null && dep.value !== '';
                }
                return false;
            })
            .filter(p => p.value != null && p.value !== '' && !p.core)
            .map(p => ({ label: p.label || p.labelKey, value: String(p.value) }));
    };

    // Build association preview — prefer per-object associations[], fall back to top-level contact/company
    const contactObj = objects.find(o => o.objectType === 'contact');
    const companyObj = objects.find(o => o.objectType === 'company');
    const contactName = contactObj?.displayName || null;
    const companyName = companyObj?.displayName || null;
    const anyObjectHasAssociations = objects.some(o => (o.associations || []).length > 0);
    const hasAssociations = anyObjectHasAssociations || (objects.length > 1 && (contactName || companyName));

    return (
        <Fade in={true}>
            <Box sx={{ width: '100%' }}>
                {oneShotMode && (
                    <Alert
                        data-testid="oneshot-banner"
                        icon={<BoltIcon />}
                        sx={{
                            mb: 2, borderRadius: '12px',
                            backgroundColor: `${ACCENT}15`, color: ACCENT,
                            border: `1px solid ${ACCENT}30`,
                        }}
                    >
                        {t('oneShotSuccess')}
                    </Alert>
                )}

                <Stack spacing={1.5}>
                    {objects.map((obj, i) => {
                        const fields = getFields(obj);
                        /* TODO: temporarily hidden
                        const attachmentCount = obj.objectType === 'email'
                            ? (obj.metadata?.attachments || []).filter(Boolean).length
                            : 0;
                        */
                        const icon = ENTITY_ICONS[obj.objectType] || <CheckCircleIcon sx={{ fontSize: 18 }} />;
                        const objectKey = obj.clientObjectId || `${obj.objectType}-${i}`;

                        return (
                            <Fade in={true} key={objectKey} style={{ transitionDelay: `${i * 80}ms` }}>
                                <Box
                                    data-testid={`review-card-${objectKey}`}
                                    sx={{ ...glassCard, p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}
                                >
                                    <Box sx={{ color: ACCENT, display: 'flex', alignItems: 'center' }}>{icon}</Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="caption" sx={{ color: ACCENT, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.68rem' }}>
                                            {t(obj.objectType)}
                                        </Typography>
                                        {obj.displayName && (
                                            <Typography variant="body2" sx={{ color: TEXT_PRIMARY, fontWeight: 500, fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {obj.displayName}
                                            </Typography>
                                        )}
                                        {fields.slice(1).map((field, fi) => (
                                            <Typography key={fi} variant="body2" sx={{ color: TEXT_SECONDARY, fontSize: '0.78rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {field}
                                            </Typography>
                                        ))}
                                        {/* TODO: temporarily hidden
                                        {attachmentCount > 0 && (
                                            <Typography variant="body2" sx={{ color: TEXT_SECONDARY, fontSize: '0.78rem' }}>
                                                {t('attachments')}: {attachmentCount}
                                            </Typography>
                                        )}
                                        */}
                                        {oneShotMode && (() => {
                                            const extras = [];
                                            if (obj.objectType === 'deal' && obj.metadata?.pipeline?.label) {
                                                extras.push({ label: t('pipeline'), value: `${obj.metadata.pipeline.label}${obj.metadata.stage?.label ? ' → ' + obj.metadata.stage.label : ''}` });
                                            }
                                            extras.push(...getMandatoryFilled(obj));
                                            if (extras.length === 0) return null;
                                            return (
                                                <Box sx={{ mt: 0.5 }}>
                                                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', my: 0.5 }} />
                                                    {extras.map((p, idx) => (
                                                        <Typography key={idx} variant="body2" sx={{ color: TEXT_TERTIARY, fontSize: '0.75rem' }}>
                                                            {t(p.label) || p.label}: <span style={{ color: TEXT_SECONDARY }}>{p.value}</span>
                                                        </Typography>
                                                    ))}
                                                </Box>
                                            );
                                        })()}
                                    </Box>
                                    <IconButton
                                        size="small"
                                        data-testid={`review-edit-${objectKey}`}
                                        aria-label={`${t('edit')} ${t(obj.objectType)}`}
                                        onClick={() => onEditStep(i)}
                                        sx={{ color: TEXT_SECONDARY, minWidth: 44, minHeight: 44 }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Fade>
                        );
                    })}
                </Stack>

                {hasAssociations && (
                    <Box sx={{ mt: 2, ...glassCard, p: 1.5, border: `1px solid ${ACCENT}20` }}>
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                            <LinkIcon sx={{ fontSize: 16, color: ACCENT }} />
                            <Typography variant="caption" sx={{ color: ACCENT, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.68rem' }}>
                                {t('associationsPreview')}
                            </Typography>
                        </Stack>
                        <Stack spacing={0.5}>
                            {objects.filter(o => o.objectType !== 'contact' && o.objectType !== 'company').map((obj) => {
                                const assocs = obj.associations || [];
                                const assocNames = assocs.map(a => a.displayName).filter(Boolean);
                                // Fall back to top-level contact/company if no per-object associations
                                const displayText = assocNames.length > 0
                                    ? assocNames.join(', ')
                                    : [contactName, companyName].filter(Boolean).join(', ');
                                if (!displayText) return null;
                                return (
                                    <Typography key={`${obj.clientObjectId || obj.objectType}`} variant="body2" sx={{ color: TEXT_SECONDARY, fontSize: '0.78rem', pl: 1 }}>
                                        {t(obj.objectType)} → {displayText}
                                    </Typography>
                                );
                            })}
                            {contactName && companyName && (
                                <Typography variant="body2" sx={{ color: TEXT_SECONDARY, fontSize: '0.78rem', pl: 1 }}>
                                    {t('contact')} → {companyName}
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                )}

                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <Button
                        startIcon={<AddIcon />}
                        data-testid="review-add-action"
                        aria-label={t('addAction')}
                        onClick={() => setAddDialogOpen(true)}
                        sx={{
                            minHeight: 44, borderRadius: '12px', textTransform: 'none',
                            color: ACCENT, border: `1px dashed ${ACCENT}50`, px: 3,
                            '&:hover': { backgroundColor: `${ACCENT_DARK}15` },
                            ...noMotion,
                        }}
                    >
                        {t('addAction')}
                    </Button>
                </Box>

                <Typography
                    variant="body2"
                    data-testid="review-count"
                    sx={{ color: TEXT_TERTIARY, textAlign: 'center', mt: 2, fontSize: '0.82rem' }}
                >
                    {t('actionsToCreate', { count: objects.length })}
                </Typography>

                <Dialog
                    open={addDialogOpen}
                    onClose={() => setAddDialogOpen(false)}
                    slotProps={{ paper: { sx: DIALOG_PAPER } }}
                >
                    <DialogContent>
                        <Typography variant="subtitle1" sx={{ color: TEXT_PRIMARY, mb: 2, fontWeight: 600 }}>
                            {t('addAction')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {ADD_ENTITY_TYPES.map((et) => (
                                <Chip
                                    key={et.key}
                                    icon={et.icon}
                                    label={t(et.key)}
                                    data-testid={`add-entity-${et.key}`}
                                    onClick={() => {
                                        setAddDialogOpen(false);
                                        if (onAddEntity) onAddEntity(et.key);
                                    }}
                                    sx={{
                                        minHeight: 44, borderRadius: '12px',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        backgroundColor: 'rgba(255,255,255,0.04)',
                                        color: TEXT_PRIMARY,
                                        '& .MuiChip-icon': { color: ACCENT },
                                        '&:hover': { backgroundColor: `${ACCENT_DARK}20` },
                                        ...noMotion,
                                    }}
                                />
                            ))}
                        </Box>
                    </DialogContent>
                </Dialog>
            </Box>
        </Fade>
    );
};

export default ReviewStepV2;
